import { ContentBand, Inner, KakaoButton, SectionHeader, SpecialTourLayout } from "./SpecialTourLayout";

const parkGolfHighlights = [
  {
    title: "부담 없이 즐기는 필드형 라운드",
    description: "넓은 잔디와 완만한 동선 중심이라 골프 경험이 많지 않은 분도 여행 일정에 편하게 넣기 좋습니다.",
  },
  {
    title: "북해도 자연과 함께하는 일정",
    description: "마쿠베츠와 토카치 지역의 탁 트인 풍경을 보며, 라운드와 휴식을 한 번에 묶을 수 있습니다.",
  },
  {
    title: "온천·식사·관광까지 연결",
    description: "파크골프만 따로 보는 것이 아니라 숙박, 온천, 저녁 동선까지 저희가 함께 정리해 드립니다.",
  },
];

const parkGolfGallery = [
  {
    title: "토카치 필드 전경",
    image: "/special-tour-assets/park-golf-field-hero.jpg",
    alt: "북해도 토카치 파크골프 필드 전경",
    description: "시야가 넓고 지역 분위기가 잘 살아나는 대표 필드 컷으로 선별했습니다.",
    className: "md:col-span-2",
    imageClassName: "h-80 object-cover",
  },
  {
    title: "필드 구성 이미지",
    image: "/special-tour-assets/park-golf-field-collage-1.png",
    alt: "북해도 파크골프 필드 구성 이미지",
    description: "여러 코스 장면을 한 번에 확인할 수 있어 첫 상담용 자료로 보기 좋습니다.",
    className: "",
    imageClassName: "h-80 object-contain bg-[#eef3eb]",
  },
  {
    title: "라운드 동선",
    image: "/special-tour-assets/park-golf-field-collage-2.png",
    alt: "북해도 파크골프 라운드 동선 이미지",
    description: "그린 주변과 이동 동선을 함께 보여주는 이미지입니다.",
    className: "",
    imageClassName: "h-72 object-contain bg-[#eef3eb]",
  },
  {
    title: "팀 라운드 분위기",
    image: "/special-tour-assets/park-golf-field-collage-3.png",
    alt: "북해도 파크골프 팀 라운드 분위기",
    description: "여럿이 함께 걷고 플레이하는 파크골프 특유의 가벼운 분위기를 보여줍니다.",
    className: "",
    imageClassName: "h-72 object-contain bg-[#eef3eb]",
  },
  {
    title: "현장 이동 컷",
    image: "/special-tour-assets/park-golf-field-walk.png",
    alt: "북해도 파크골프 현장 이동 장면",
    description: "필드 현장감을 담은 보조 이미지로, 실제 여행 느낌을 더합니다.",
    className: "",
    imageClassName: "h-72 object-cover",
  },
];

export function ParkGolfPage() {
  return (
    <SpecialTourLayout>
      <section className="bg-[#102517] text-white">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-10 sm:px-6 md:grid-cols-[minmax(280px,0.72fr)_1fr] md:items-center lg:px-8">
          <div className="overflow-hidden rounded-lg border border-white/15 bg-black shadow-2xl">
            <video
              src="/special-tour-assets/park-golf-hokkaido-tour.mp4"
              poster="/special-tour-assets/park-golf-hokkaido-tour-cover.jpg"
              className="h-[520px] w-full object-cover md:h-[620px]"
              autoPlay
              muted
              loop
              playsInline
              controls
            >
              파크골프 홍보 영상을 재생할 수 없습니다.
            </video>
          </div>

          <div className="py-4">
            <p className="mb-3 text-sm font-900 uppercase tracking-normal text-[#f1c8aa]">Park Golf</p>
            <h1 className="max-w-3xl text-4xl font-900 leading-tight [word-break:keep-all] md:text-6xl">
              파크골프의 발원지 북해도 마쿠베츠(幕別)
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-8 text-white/78 [word-break:keep-all] md:text-lg">
              마쿠베츠 파크골프의 필드 분위기와 북해도 여행 감성을 보기 좋게 담았습니다. 골프가 부담스러운
              동행자도 함께 즐기기 좋은 여유로운 일정입니다.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <KakaoButton label="파크골프 견적 문의" />
              <a
                href="#park-golf-gallery"
                className="inline-flex min-h-12 items-center justify-center rounded-lg border border-white/25 px-5 text-sm font-900 text-white transition hover:bg-white/10"
              >
                대표 이미지 보기
              </a>
            </div>

            <div className="mt-10 grid gap-3 sm:grid-cols-3">
              {[
                ["3박4일", "숙박형 일정"],
                ["126홀·135홀", "상품 자료 기준"],
                ["온천·관광", "연계 상담"],
              ].map(([value, label]) => (
                <div key={value} className="rounded-lg border border-white/12 bg-white/8 p-4">
                  <div className="text-2xl font-900 text-[#f6d66a]">{value}</div>
                  <div className="mt-1 text-sm font-700 text-white/68">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <ContentBand className="bg-white">
        <Inner>
          <SectionHeader
            eyebrow="Why Park Golf"
            title="파크골프를 여행 일정에 넣으면 좋은 이유"
            body="일반 골프 라운드와는 다른 가벼움이 있어, 가족·지인·비골퍼 동행자가 함께 움직이는 팀에 특히 잘 맞습니다."
          />
          <div className="grid gap-5 md:grid-cols-3">
            {parkGolfHighlights.map((item) => (
              <article key={item.title} className="rounded-lg border border-[#dce3d8] bg-[#fbfaf6] p-6 shadow-sm">
                <h2 className="text-2xl font-900 leading-tight text-[#15391f] [word-break:keep-all]">{item.title}</h2>
                <p className="mt-4 text-sm leading-7 text-[#596559] [word-break:keep-all]">{item.description}</p>
              </article>
            ))}
          </div>
        </Inner>
      </ContentBand>

      <ContentBand id="park-golf-gallery">
        <Inner>
          <SectionHeader
            eyebrow="Selected Images"
            title="북해도 파크골프 대표 이미지"
            body="실제 필드 분위기가 잘 보이는 이미지와 상담에 도움이 되는 자료 이미지를 선별했습니다. 이미지를 클릭하면 크게 볼 수 있습니다."
          />
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {parkGolfGallery.map((item) => (
              <article
                key={item.title}
                className={`overflow-hidden rounded-lg border border-[#dce3d8] bg-white shadow-sm ${item.className}`}
              >
                <img src={item.image} alt={item.alt} className={`w-full ${item.imageClassName}`} />
                <div className="p-6">
                  <h2 className="text-2xl font-900 leading-tight text-[#15391f] [word-break:keep-all]">{item.title}</h2>
                  <p className="mt-3 text-sm leading-7 text-[#596559] [word-break:keep-all]">{item.description}</p>
                </div>
              </article>
            ))}
          </div>
        </Inner>
      </ContentBand>

      <ContentBand className="bg-white">
        <Inner>
          <SectionHeader
            eyebrow="Planning Materials"
            title="북해도 파크골프 상담 자료"
            body="지도와 홍보 자료를 함께 배치해, 어디로 이동하고 어떤 일정으로 구성하는지 빠르게 이해할 수 있도록 정리했습니다."
          />
          <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
            <article className="overflow-hidden rounded-lg border border-[#dce3d8] bg-[#fbfaf6] shadow-sm">
              <div className="flex h-72 items-center justify-center bg-white p-4">
                <img
                  src="/special-tour-assets/park-golf-hokkaido-map.png"
                  alt="북해도 파크골프 이동 지도 자료"
                  className="max-h-full w-full object-contain"
                />
              </div>
              <div className="p-6">
                <h2 className="text-2xl font-900 text-[#15391f]">북해도 이동 이미지</h2>
                <p className="mt-3 text-sm leading-7 text-[#596559] [word-break:keep-all]">
                  파크골프와 오비히로, 토카치 일대를 함께 보는 상담용 지도 자료입니다.
                </p>
              </div>
            </article>

            <article className="overflow-hidden rounded-lg border border-[#dce3d8] bg-[#fbfaf6] shadow-sm">
              <div className="flex h-72 items-center justify-center bg-white p-4">
                <img
                  src="/special-tour-assets/park-golf-promotion-sheet.jpg"
                  alt="북해도 마쿠베츠 파크골프 홍보 자료"
                  className="max-h-full w-full object-contain"
                />
              </div>
              <div className="p-6">
                <h2 className="text-2xl font-900 text-[#15391f]">마쿠베츠 파크골프 상품 자료</h2>
                <p className="mt-3 text-sm leading-7 text-[#596559] [word-break:keep-all]">
                  3박4일 126홀·135홀형 파크골프 일정은 항공, 숙소, 현지 가능 여부에 따라 상담 시 다시 확인해 드립니다.
                </p>
              </div>
            </article>
          </div>
        </Inner>
      </ContentBand>

      <ContentBand>
        <Inner>
          <div className="grid gap-6 rounded-lg border border-[#dce3d8] bg-white p-6 shadow-sm md:grid-cols-[1fr_auto] md:items-center md:p-8">
            <div>
              <h2 className="text-3xl font-900 leading-tight text-[#15391f] [word-break:keep-all]">
                파크골프와 일반 골프를 함께 묶어 상담해 드립니다
              </h2>
              <p className="mt-4 max-w-3xl text-base leading-8 text-[#596559] [word-break:keep-all]">
                팀 구성에 따라 일반 골프 라운드, 파크골프, 온천, 관광을 섞어 더 편한 북해도 여행 일정으로 조정할 수 있습니다.
              </p>
            </div>
            <KakaoButton label="파크골프 일정 상담하기" />
          </div>
        </Inner>
      </ContentBand>
    </SpecialTourLayout>
  );
}
